import { useEffect } from 'react'
import { createRootRoute, Outlet, useLocation, useMatches, useNavigate, useSearch } from '@tanstack/react-router'
import { Tooltip } from '@/common/components/tooltip'
import { ToastProvider } from '@/common/components/toast'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { getDefaultRouteForRole } from '@/common/utils/getDefaultRouteForRole'
import { dynamicPath, parseRedirectPath } from '@/common/utils/navigation'

const LOGGED_IN_KEY = 'logged_in'

export const Route = createRootRoute({
  component: RootComponent,
})

/**
 * Handles cross-tab auth sync via localStorage and all auth-related redirects.
 * Must be inside RouterProvider to use router hooks.
 */
function AuthSyncAndRedirect() {
  const { isAuthenticated, isLoading, roles, verifySession, clearSession } = useAuth()
  const {
    needsOnboarding,
    needsNewAgreement,
    needsExternalAgentTerms,
    refetch: refetchProfile,
  } = useUserProfile()
  const location = useLocation()
  const matches = useMatches()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { redirect?: string }

  // Check if current route is under _public layout (derived from route tree)
  const isPublicRoute = matches.some((match) => match.routeId === '/_public')

  // Sync logged_in localStorage with auth state
  // Values: null (not logged in), 'onboarding' (needs onboarding), 'agreement' (needs agreement), 'true' (fully logged in)
  useEffect(() => {
    if (isLoading) return

    if (isAuthenticated) {
      const value = needsOnboarding ? 'onboarding' : needsNewAgreement ? 'agreement' : 'true'
      localStorage.setItem(LOGGED_IN_KEY, value)
    } else {
      localStorage.removeItem(LOGGED_IN_KEY)
    }
  }, [isAuthenticated, isLoading, needsOnboarding, needsNewAgreement])

  // Cross-tab sync: detect when another tab's auth state differs from ours
  // Storage values: null (logged out), 'onboarding' (needs onboarding), 'true' (fully logged in)
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key !== LOGGED_IN_KEY) return

      const newValue = event.newValue
      const oldValue = event.oldValue
      const isLoggedIn = newValue === 'true' || newValue === 'onboarding' || newValue === 'agreement'

      if (isLoggedIn && !isAuthenticated) {
        // Another tab logged in, we're not authenticated - try to restore session
        await verifySession()
      } else if (!isLoggedIn && isAuthenticated) {
        // Another tab logged out, we're still authenticated - clear our session
        clearSession()
      } else if (oldValue === 'onboarding' && newValue === 'true' && needsOnboarding) {
        // Another tab completed onboarding - refetch profile to update needsOnboarding
        refetchProfile()
      } else if (oldValue === 'agreement' && newValue === 'true' && needsNewAgreement) {
        // Another tab signed the agreement - refetch profile to update needsNewAgreement
        refetchProfile()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [isAuthenticated, verifySession, clearSession, needsOnboarding, needsNewAgreement, refetchProfile])

  // Handle redirects based on auth state
  useEffect(() => {
    if (isLoading) return

    if (!isAuthenticated && !isPublicRoute && location.pathname !== '/login') {
      // Not authenticated on protected route → redirect to login with return path
      // Extra pathname check prevents redirect loop when isPublicRoute is stale during navigation
      const redirectPath = location.pathname + location.searchStr
      navigate({ to: '/login', search: { redirect: redirectPath } })
    } else if (isAuthenticated && location.pathname === '/login') {
      // Authenticated on login page → redirect to intended destination.
      // Split out any `?key=value` so TanStack's path-only `to` doesn't
      // drop it — email deep-links (e.g.
      // `/properties/$id/offers?offerId=…`) depend on the query
      // surviving the login round-trip.
      const redirectTo = search.redirect || getDefaultRouteForRole(roles)
      const { pathname, search: searchParams } = parseRedirectPath(redirectTo)
      navigate({ to: dynamicPath(pathname), search: searchParams, replace: true })
    } else if (isAuthenticated && needsOnboarding && location.pathname !== '/onboarding') {
      // Authenticated but needs onboarding → redirect to onboarding with return path
      const redirectPath = location.pathname + location.searchStr
      navigate({ to: '/onboarding', search: { redirect: redirectPath } })
    } else if (
      isAuthenticated &&
      needsExternalAgentTerms &&
      location.pathname !== '/onboarding'
    ) {
      // MRG-281: External agent has finished basic onboarding but hasn't
      // signed the Marketplace Agent Agreement yet. Bounce them back to
      // `/onboarding` so the extTerms step can render — without this they
      // could land on `/properties` and start the training tour before
      // accepting the agreement (e.g. legacy agents whose
      // `onboarding_completed_at` predates the extTerms gate).
      const redirectPath = location.pathname + location.searchStr
      navigate({ to: '/onboarding', search: { redirect: redirectPath } })
    } else if (isAuthenticated && needsNewAgreement && location.pathname !== '/agreement') {
      // Authenticated, onboarding complete, but needs new agreement → redirect to agreement gate
      const redirectPath = location.pathname + location.searchStr
      navigate({ to: '/agreement', search: { redirect: redirectPath } })
    }
  }, [isAuthenticated, isLoading, roles, needsOnboarding, needsExternalAgentTerms, needsNewAgreement, isPublicRoute, location.pathname, location.searchStr, search.redirect, navigate])

  return null
}

function RootComponent() {
  return (
    <Tooltip.Provider delayDuration={300}>
      <ToastProvider>
        <AuthSyncAndRedirect />
        <Outlet />
      </ToastProvider>
    </Tooltip.Provider>
  )
}
